import { RichTextDSRenderer } from '@src/components/blocks/rich-text-ds'
import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'

import { buildWidgetTrackingMetaData, protoButtonToDomain, toRichText } from '../widget-utils'

import type { RichTextWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// @i18n-ignore
const RICH_TEXT_DS_WIDGET_TEST_ID = 'rich-text-ds-widget'

/**
 * DS branch of the PL `RICH_TEXT` widget.
 *
 * Section-level dynamic classes (`baseDynamicClasses` / `mobileDynamicClassesOverrides`)
 * are forwarded to `RichTextDSRenderer` which applies them to the section wrapper via
 * scoped `--rt-ds-s-*` CSS custom properties (section scope prevents inheritance into items).
 *
 * Per-item dynamic classes (`baseDynamicValueClasses` / `dynamicValueClassesMobile`)
 * are carried on each `RichText` domain object (populated by `toRichText`) and consumed
 * by `RichTextDSRenderer` per-item via scoped `--rt-ds-i-*` CSS custom properties.
 *
 * `data.title` is **not** rendered as visible copy (legacy didn't render it either —
 * the SCSS `RichTextSection` only used `title` to label button-click telemetry).
 *
 * `bringInViewHash` is not part of `RichTextWidgetData` (it lives on the Contentful
 * `CustomSection` only), so the DS path has no scroll-anchor concern here.
 *
 * `classNamesMobileOverrides` is a `CustomSection` domain field absent from
 * `RichTextWidgetData` proto — legacy always passed `[]`, so omitting it here is parity.
 */
export function RichTextDSWidget(props: PLTypedWidgetProps<RichTextWidgetData>) {
  const data = (props.data as { richTextData?: RichTextWidgetData }).richTextData
  if (!data) return null

  const richTexts = data.richTexts
    .map(rt => toRichText(rt))
    .filter((rt): rt is NonNullable<typeof rt> => rt !== undefined)

  const buttons = data.buttons.map(rawButton => protoButtonToDomain(rawButton as Record<string, unknown>))

  return (
    <RichTextDSRenderer
      id={props.id || ''}
      title={data.title}
      sectionType={data.sectionType}
      richTexts={richTexts}
      buttons={buttons}
      classNames={data.classNames}
      baseDynamicClasses={data.baseDynamicClasses}
      mobileDynamicClassesOverrides={data.mobileDynamicClassesOverrides}
      trackingMetaData={buildWidgetTrackingMetaData(data.analytics)}
      testId={RICH_TEXT_DS_WIDGET_TEST_ID}
      renderEmpty
    />
  )
}
